import { useEffect, useMemo, useState } from 'react';
import { UseRecurlyInstance } from '@recurly/react-recurly';
import { ApplePayInstance } from 'recurly.js';
import { loadApplePay, showApplePay } from '../utils/applePay';

interface ApplePayProps {
  recurly: UseRecurlyInstance;
  checkoutTotal?: number;
  onApplePayVerify: (token: string) => void;
  onApplePayError: (error: unknown) => void;
  onApplePayCancel: () => void;
  flags: unknown;
}

export const APPLE_PAY_POLICY_LABEL =
  'This will be your method of payment for all of your purchases including your Seasonal or Annual membership fees and sale purchases.';

const useApplePay = ({
  recurly,
  checkoutTotal,
  onApplePayVerify,
  onApplePayError,
  onApplePayCancel,
  flags,
}: ApplePayProps) => {
  const [applePayLoaded, setApplePayLoaded] = useState(false);
  const [showApplePayOption, setShowApplePayOption] = useState(false);
  const applePay = useMemo<ApplePayInstance>(() => {
    const applePayInstance = recurly.ApplePay({
      country: 'US',
      currency: 'USD',
      label: APPLE_PAY_POLICY_LABEL,
      total: checkoutTotal?.toString() || '0',
    });
    window.applePay = applePayInstance;
    return applePayInstance;
  }, [checkoutTotal]);

  useEffect(() => {
    const isApplePayAvailable = showApplePay(flags, setShowApplePayOption, showApplePayOption);
    if (!applePayLoaded) {
      loadApplePay(isApplePayAvailable, setApplePayLoaded);
    }
  }, [applePayLoaded]);

  useEffect(() => {
    if (applePay) {
      applePay.on('token', (token: unknown) => {
        if (token?.id) {
          onApplePayVerify(token.id);
        } else onApplePayError(new Error('Apple Pay token is missing'));
      });

      applePay.on('error', (err) => {
        onApplePayError(err);
      });

      applePay.on('cancel', () => {
        onApplePayCancel();
      });
    }
  }, [applePay]);
  return { showApplePayOption };
};

export default useApplePay;
